<template>
  <div class="chatbot-wrapper">
    <TacticsStyles v-if="displayTacticContextFilters" />
    <div class="chatbot">
      <div v-if="messages.length" ref="content" class="scrollable-content">
        <Node v-for="(conv, index) in messages" :key="index">
          <ErrorMessage v-if="isErrorMessage(conv.messageType)" :message="conv.message" />
          <Message v-else-if="!isContextMessage(conv.messageType)" :message="conv" />
        </Node>
      </div>
    </div>
    <MessageInput @onSubmit="onSubmitValue" />
  </div>
</template>

<script>
import { computed, nextTick, onUpdated, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { messageType, queryType } from '~/constants/conversationModels';

import Message from '~/components/conversation/chatMessage/Message';
import Node from '~/components/conversation/chatMessage/Node';
import MessageInput from '~/components/conversation/MessageInput';
import useConversations from '~/composables/useConversations';
import ErrorMessage from '~/components/conversation/chatMessage/ErrorMessage.vue';
import useAuthoring from '~/composables/useAuthoring';
import TacticsStyles from '~/components/conversation/authoring/tacticsContexts/TacticContextAppliedStylesInConversation.vue';

export default {
  name: 'Conversation',
  components: {
    ErrorMessage,
    MessageInput,
    Message,
    Node,
    TacticsStyles,
  },

  setup() {
    const route = useRoute();
    const currentRouteConversationId = ref(route.params.conversationId);

    const { currentConversation, publishToConversation } = useConversations();
    const { tacticContextFilters } = useAuthoring();

    const messages = computed(() => currentConversation.value.messages);

    const conversationId = computed(() => currentConversation.value.conversationId);

    const nrOfContexts = computed(() => Object.values(tacticContextFilters.value).length);

    const content = ref(null);

    const isErrorMessage = (type) => type === messageType.error;
    const isContextMessage = (type) => type === messageType.context;

    const scrollToBottom = () => {
      if (content.value) {
        content.value.scrollTop = content.value.scrollHeight;
      }
    };

    const onSubmitValue = async (value) => {
      publishToConversation(conversationId.value, queryType.authoringQuery, value);
    };

    onUpdated(() => {
      const isTyping = messages.value.some((message) => message.isTyping);

      if (isTyping) {
        // Use nextTick to wait for the DOM to update
        nextTick(() => {
          scrollToBottom();
        });
      }
    });

    watch(
      () => route.params.conversationId,
      async () => {
        // we'll need the value of conversationId from route in order to pass it to unsubscribe;
        // onBeforeUnmount can't see the correct value from route.params.conversationId if you navigate to /authoring
        currentRouteConversationId.value = route.params.conversationId;
      },
      { immediate: true, deep: true },
    );

    watch(
      () => messages.value[messages.value.length - 1] && messages.value[messages.value.length - 1].data?.length,
      (newLength, oldLength) => {
        if (newLength !== oldLength) {
          // Ensures scroll to bottom when references are concatenated to the last message created by combining chunks
          nextTick(() => {
            scrollToBottom();
          });
        }
      },
      { deep: true },
    );

    watch(
      () => messages.value.length,
      (newLength, oldLength) => {
        if (newLength !== oldLength) {
          // Ensures scroll to bottom when component is mounted for the first time and new messages are injected into conversation(system messages)
          nextTick(() => {
            scrollToBottom();
          });
        }
      },
      { deep: true },
    );

    return {
      content,
      isContextMessage,
      isErrorMessage,
      messages,
      onSubmitValue,
      displayTacticContextFilters: nrOfContexts,
    };
  },
};
</script>

<style lang="scss" scoped>
//@import '../../assets/styles/variables.scss';

.chatbot-wrapper {
  --primary-color: #{$bb-primary-1};
  --primary-dimmed-color: #{$bb-primary-1-dimmed};

  @include bb-base-font-family;
  @include bb-regular-text-styles('md');

  @include media-breakpoint-up('sm') {
  }

  // height: 100%;

  // color: $bb-neutral-1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: antialiased;
  font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%; /* Prevent adjustments of font size after orientation changes in iOS */

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-width: 524px;
  // margin: auto;
  padding: $bb-spacing-5;
  background-color: white;
  flex: 1;
  border-radius: $bb-spacing-4;
  box-shadow: 0px 5px 8px 0px rgba(0, 0, 0, 0.1);
  overflow: auto;

  .chatbot {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    justify-content: flex-end;
    margin-bottom: $bb-spacing-2;
  }

  .scrollable-content {
    overflow: auto;
    // max-height: 100%;
    padding-right: $bb-spacing-4;

    //display: flex;
    //align-items: flex-end;
    //flex-wrap: wrap;
    //height: 100%;

    //&__inner {
    //width: 100%;
    //}
  }
}
</style>
